import { useQuery } from "@tanstack/react-query";
import type { RefObject } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import {
  createSideMenuMediator,
  isSideMenuEvent,
  type SideMenuEffect,
  type SideMenuInputEvent,
  type SideMenuState,
} from "@/app/layouts/sideMenuMediator";
import { boardPublicPath } from "@/config/browserBoard";
import type { ThreadSummary } from "@/entities/thread";
import { useAnnouncementStatus } from "@/features/announce/hooks/useAnnouncementStatus";
import { historyThreadKey } from "@/features/history/historyIdentity";
import {
  type HistoryMode,
  historyModeLabel,
} from "@/features/history/historyMode";
import {
  selectHistoryItemsForScope,
  useHistorySummaryScope,
} from "@/features/history/hooks/historySummaryScope";
import { fetchHistoryThreadSummaries } from "@/features/history/hooks/useHistoryThreads";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import {
  selectActivePostedItems,
  usePostedHistoryStore,
} from "@/features/history/stores/postedHistoryStore";
import { useSettingsStore } from "@/features/settings/hooks";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import { buildForcePcUrl } from "@/shared/lib/forcePc";
import { createEscapeKeyHandler } from "@/shared/lib/keyboardAccessibility";
import {
  BOARD_RULES_HREF,
  MIX_CATALOG_HREF,
  useFromMixCatalog,
} from "@/shared/lib/mixReturn";
import {
  getHierarchyResourceSnapshot,
  useComponentNode,
  useHierarchyResource,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { useDialogFocusTrap } from "@/shared/ui/overlay/dialogFocus";

export type SideMenuCatalogLink =
  | { readonly kind: "href"; readonly href: string }
  | { readonly kind: "to"; readonly to: string };

export interface SideMenuHistoryEntry {
  readonly key: string;
  readonly item: { readonly boardSlug: string; readonly threadId: string };
  readonly thread: ThreadSummary;
  readonly unreadCount: number | undefined;
}

export interface SideMenuPresentation {
  readonly node: EventNode<TypedEvent>;
  readonly isOpen: boolean;
  readonly dialogRef: RefObject<HTMLElement | null>;
  readonly catalogLink: SideMenuCatalogLink;
  readonly boardRulesHref: string;
  readonly historyHref: string;
  readonly forcePcUrl: string;
  readonly historyMode: HistoryMode;
  readonly historyModeText: string;
  readonly historyItemsCount: number;
  readonly historyThreadsCount: number;
  readonly historyEntries: readonly SideMenuHistoryEntry[];
  readonly isHistoryError: boolean;
  readonly isHistoryFetching: boolean;
  readonly jukeboxEnabled: boolean;
  readonly hasUnreadAnnouncements: boolean;
  readonly onEvent: (event: SideMenuInputEvent) => void;
}

export interface SideMenuPresenterOptions {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

export function useSideMenuPresenter({
  isOpen,
  onClose,
}: SideMenuPresenterOptions): SideMenuPresentation {
  const onCloseRef = useRef(onClose);
  const refetchHistoryRef = useRef<() => void>(() => undefined);
  const historyState = useRef({ itemsCount: 0, isFetching: false });
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const mediator = useHierarchyResource(
    (previous) =>
      createSideMenuMediator(
        {
          getHistoryState: () => historyState.current,
          runEffect(effect: SideMenuEffect): void {
            if (effect.type === "close") {
              onCloseRef.current();
            } else {
              refetchHistoryRef.current();
            }
          },
        },
        previous?.isDisposed
          ? getHierarchyResourceSnapshot<SideMenuState>(previous)
          : undefined,
      ),
    [],
  );
  const state = useMediatorSnapshot(mediator);
  const node = useComponentNode((event: TypedEvent) => {
    if (!isSideMenuEvent(event)) return "unhandled";
    const result = mediator.dispatch(event);
    return result.status === "handled" ? "handled" : "unhandled";
  });
  const onEvent = useCallback(
    (event: SideMenuInputEvent): void => {
      node.dispatch(event);
    },
    [node],
  );
  const requestClose = useCallback((): void => {
    onEvent({ type: "side-menu.close-requested" });
  }, [onEvent]);

  useEffect(() => {
    return (): void => requestClose();
  }, [requestClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = createEscapeKeyHandler(requestClose);
    window.addEventListener("keydown", handleKeyDown);
    return (): void => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, requestClose]);

  const dialogRef = useDialogFocusTrap<HTMLElement>(isOpen);
  const getUnreadCount = useHistoryStore((store) => store.getUnreadCount);
  const viewed = useHistoryStore((store) => store.viewed);
  const posted = usePostedHistoryStore((store) => store.posted);
  const fromMixCatalog = useFromMixCatalog();
  const historyScope = useHistorySummaryScope();
  const historyItems = selectHistoryItemsForScope(
    state.historyMode === "posted" ? selectActivePostedItems(posted) : viewed,
    historyScope,
  )
    .slice(0, 20)
    .map(({ boardSlug, threadId }) => ({ boardSlug, threadId }));
  const jukeboxEnabled = useSettingsStore(
    (settings) => settings.jukeboxEnabled,
  );
  const { hasUnreadAnnouncements } = useAnnouncementStatus();

  const {
    data: historyThreads = [],
    isError: isHistoryError,
    isFetching: isHistoryFetching,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: [
      "history-threads",
      historyScope.slugs,
      historyScope.tagBoard,
      state.historyMode,
      historyItems,
    ],
    queryFn: async (): Promise<ThreadSummary[]> => {
      if (historyItems.length === 0) return [];
      return fetchHistoryThreadSummaries(historyScope, historyItems);
    },
    enabled: isOpen && historyItems.length > 0,
    staleTime: 60_000,
  });

  useLayoutEffect(() => {
    refetchHistoryRef.current = (): void => {
      void refetchHistory();
    };
  }, [refetchHistory]);
  useLayoutEffect(() => {
    historyState.current = {
      itemsCount: historyItems.length,
      isFetching: isHistoryFetching,
    };
  }, [historyItems.length, isHistoryFetching]);

  const historyEntries = historyItems.flatMap<SideMenuHistoryEntry>((item) => {
    const thread = historyThreads.find(
      (candidate) =>
        candidate.id === item.threadId &&
        (candidate.b ?? item.boardSlug) === item.boardSlug,
    );
    if (!thread) return [];
    return [
      {
        key: historyThreadKey(item),
        item,
        thread,
        unreadCount: getUnreadCount(
          item.boardSlug,
          thread.id,
          thread.replyCount,
        ),
      },
    ];
  });

  return {
    node,
    isOpen,
    dialogRef,
    catalogLink: fromMixCatalog
      ? { kind: "href", href: MIX_CATALOG_HREF }
      : { kind: "to", to: "/" },
    boardRulesHref: BOARD_RULES_HREF,
    historyHref: boardPublicPath("/history/viewed"),
    forcePcUrl:
      typeof window === "undefined"
        ? "/?pc=1"
        : buildForcePcUrl(window.location),
    historyMode: state.historyMode,
    historyModeText: historyModeLabel(state.historyMode),
    historyItemsCount: historyItems.length,
    historyThreadsCount: historyThreads.length,
    historyEntries,
    isHistoryError,
    isHistoryFetching,
    jukeboxEnabled,
    hasUnreadAnnouncements,
    onEvent,
  };
}
